import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock, createVNode as _createVNode, Fragment as _Fragment, withKeys as _withKeys, renderList as _renderList, renderSlot as _renderSlot, normalizeClass as _normalizeClass, createTextVNode as _createTextVNode, withCtx as _withCtx, vShow as _vShow, normalizeStyle as _normalizeStyle, withDirectives as _withDirectives, Teleport as _Teleport, resolveDirective as _resolveDirective } from "vue"

const _hoisted_1 = { class: "select__selected-options" }
const _hoisted_2 = { class: "select__input" }
const _hoisted_3 = {
  key: 0,
  class: "select__label"
}
const _hoisted_4 = {
  key: 0,
  class: "select__icons-wrapper"
}
const _hoisted_5 = { key: 0 }
const _hoisted_6 = ["onClick", "onKeyup"]
const _hoisted_7 = { class: "select__item-content" }
const _hoisted_8 = ["innerHTML"]
const _hoisted_9 = {
  key: 0,
  class: "select__item-subtitle"
}
const _hoisted_10 = {
  key: 0,
  class: "select__select-all"
}
const _hoisted_11 = {
  key: 0,
  class: "select__hint"
}
const _hoisted_12 = {
  key: 1,
  class: "select__error"
}

import type { ISelectOption, ISelectProps, SelectModelValue } from '@/@types/components/ui/forms';
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { truncateText } from '@/utils/truncateText';
import SmInput from '@/components/ui/form/SmInput/SmInput.vue';
import SmIcon from '@/components/ui/SmIcon/SmIcon.vue';
import SmTextButton from '@/components/ui/buttons/SmTextButton/SmTextButton.vue';
import DefaultPreloader from '@/components/ui/preloaders/DefaultPreloader/DefaultPreloader.vue';
import './style.scss';


export default /*#__PURE__*/_defineComponent({
  __name: 'SmSelect',
  props: {
    modelValue: { default: null },
    options: { default: () => [] },
    id: {},
    label: {},
    placeholder: {},
    hint: {},
    error: {},
    search: { type: Boolean, default: false },
    multiselect: { type: Boolean, default: false },
    loading: { type: Boolean, default: false },
    clear: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: emits }) {

const props = __props;

// eslint-disable-next-line




const instance = getCurrentInstance();
const uid = ref<number | undefined>(instance?.uid);
const rootElement = ref<HTMLElement | null>(null);
const inputElement = ref<HTMLElement | null>(null);
const listElement = ref<HTMLElement | null>(null);
const inputAltElement = ref<HTMLElement | null>(null);

const localOptions = ref<ISelectOption[]>([...props.options]);
const searchString = ref<string>('');
const hightlightFirstItem = ref<boolean>(false);
const rootElementDOMRect = ref<DOMRect | null>(null);
const searchProcess = ref(false);
const showList = ref<boolean>(false);

const id = computed(() => `select-${uid.value}`);
const name = computed(() => `select-${uid.value}`);

const isMultiselect = computed(() => props.multiselect);
const hasSearch = computed(() => props.search);
const isMultiselectWithSearch = computed(() => props.multiselect && props.search);

const selectedOption = computed<ISelectOption | null>({
  get: () => props.options.find(opt => opt.value === props.modelValue) || null,
  set: (val) => emits('update:modelValue', val?.value ?? null),
});

const selectedOptions = computed<ISelectOption[]>({
  get: () => localOptions.value.filter(opt => Array.isArray(props.modelValue) && props.modelValue.includes(opt.value)),
  set: (val) => emits('update:modelValue', val.map(v => v.value)),
});

const computeWrapperStyle = computed(() => {
  return isMultiselectWithSearch.value && selectedOptions.value.length === 0
    ? { justifyContent: 'space-between' }
    : {};
});

const classList = computed((): string => {
  const hasContent = selectedOption.value || selectedOptions.value.length;
  return [
    hasContent && 'select--contented',
    props.error?.length && 'select--error',
    showList.value && 'select--has-list',
    isMultiselectWithSearch.value && 'select--multiselect-search',
    props.disabled && 'select--disabled',
    props.dontChangeColorWhenDisabled && 'select--default-color',
  ].filter(Boolean).join(' ');
});

const showClearIcon = computed(() => props.clear && ((selectedOption.value && selectedOption.value.value !== null || selectedOptions.value.length)));

const toggleIcon = computed(() => `Chevron${showList.value ? 'Up' : 'Down'}`);
const hasLabel = computed(() => Boolean(props.label));
const hasHint = computed(() => Boolean(props.hint));
const hasError = computed(() => Boolean(props.error));

const dropdownStyles = computed(() => {
  if (!rootElementDOMRect.value) return '';
  return {
    top: `${rootElementDOMRect.value.y + rootElementDOMRect.value.height}px`,
    left: `${rootElementDOMRect.value.x}px`,
    width: `${rootElementDOMRect.value.width}px`,
  };
});

const searchSubstrings = computed(() => searchString.value?.split(' ').map(escapeRegExp) || []);

const filteredOptions = computed(() => {
  let current = [...localOptions.value];

  if (isMultiselect.value) {
    current = current.filter(opt => !selectedOptions.value.some(sel => sel.value === opt.value));
  } else if (selectedOption.value) {
    current = current.filter(opt => opt.value !== selectedOption.value?.value);
  }

  if (searchProcess.value && searchString.value) {
    current = getTextFilteredOptions(current);
  }

  return current;
});

const list = computed(() => localOptions.value);

watch(() => props.options, (newOptions) => {
  localOptions.value = [...newOptions];
});

function getSelectedOptionTitle(option: ISelectOption): string {
  return props.multiselect ? truncateText(option.title, 17, '...') : option.title;
}

function handleToggle() {
  if (props.disabled) return;
  showList.value ? onHideList() : onShowList();
}

function onShowList() {
  setRootElementDOMRect();
  addDropdownListeners();
  showList.value = true;
  if (props.search) {
    isMultiselect.value ? inputAltElement.value?.focus() : inputElement.value?.focus();
  }
}

function onHideList() {
  removeDropdownListeners();
  if (isMultiselect.value) searchString.value = '';
  searchProcess.value = false;
  showList.value = false;
}

function handleSelectItem(option?: ISelectOption) {
  if (!option) return;
  if (isMultiselect.value) {
    const alreadySelected = selectedOptions.value.some(o => o.value === option.value);
    if (!alreadySelected) {
      selectedOptions.value = [...selectedOptions.value, option];
      searchString.value = '';
    }
  } else {
    selectedOption.value = option;
    // searchString.value = option.title;
    handleToggle();
  }
}

function handleClear() {
  searchString.value = '';
  selectedOption.value = null;
  selectedOptions.value = [];

  if (props.multiselect) {
    emits('update:modelValue', []);
  } else {
    emits('update:modelValue', null);
  }
}

function handleFocusList() {
  handleToggle();
  listElement.value?.focus();
}

function handleInput() {
  hightlightFirstItem.value = true;
  searchProcess.value = true;
}

function handleInputAlt() {
  hightlightFirstItem.value = true;
  searchProcess.value = true;
}

function handleKeyupInputAlt(event: KeyboardEvent) {
  if (event.key === 'Enter') handleSelectItem(undefined);
  searchProcess.value = true;
  searchString.value = inputAltElement.value?.innerText || '';
}

function handleSelectAll() {
  searchString.value = '';
  emits('update:modelValue', localOptions.value.map(opt => opt.value));
  handleToggle();
}

function handleRemoveItem(option: ISelectOption) {
  selectedOptions.value = selectedOptions.value.filter(o => o.value !== option.value);
  emits('update:modelValue', selectedOptions.value.map(o => o.value));
}

function isItemSelected(item: ISelectOption) {
  return !!selectedOptions.value.find(opt => opt.value === item.value);
}

function isFirstItemHighlighted(index: number) {
  return index === 0 && hightlightFirstItem.value;
}

function setRootElementDOMRect() {
  if (rootElement.value) {
    rootElementDOMRect.value = rootElement.value.getBoundingClientRect();
  }
}

function addDropdownListeners() {
  document.addEventListener('scroll', setRootElementDOMRect);
  window.addEventListener('resize', setRootElementDOMRect);
}

function removeDropdownListeners() {
  document.removeEventListener('scroll', setRootElementDOMRect);
  window.removeEventListener('resize', setRootElementDOMRect);
}

function escapeRegExp(string: string) {
  return string.replace(/[/\\^$*+?.()|[\]{}]/g, '\\$&');
}

function getTextFilteredOptions(options: ISelectOption[]) {
  const searchRegexp = new RegExp(searchSubstrings.value.map(s => `(?=.*${s.toLowerCase()})`).join(''), 'g');

  return options.filter(opt => {
    if (opt.value === null) return true; // <== не фильтруем "Все лицевые счета"
    return opt.title.toLowerCase().match(searchRegexp);
  }).map(opt => {
    if (opt.value === null) return opt;
    const regex = new RegExp(searchSubstrings.value.join('|'), 'gi');
    return {
      ...opt,
      title: opt.title.replace(regex, match => `<strong>${match}</strong>`),
    };
  });
}

return (_ctx: any,_cache: any) => {
  const _directive_click_outside = _resolveDirective("click-outside")!

  return _withDirectives((_openBlock(), _createElementBlock("div", {
    ref_key: "rootElement",
    ref: rootElement,
    class: _normalizeClass(["select", classList.value]),
    onClick: _cache[3] || (_cache[3] = _withModifiers(() => {}, ["stop"])),
    onResize: setRootElementDOMRect
  }, [
    _createElementVNode("div", {
      class: "select__wrapper",
      style: _normalizeStyle(computeWrapperStyle.value),
      onClick: handleToggle
    }, [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("span", _hoisted_2, _toDisplayString(selectedOption.value?.title), 1),
        (hasLabel.value)
          ? (_openBlock(), _createElementBlock("label", _hoisted_3, _toDisplayString(_ctx.label), 1))
          : _createCommentVNode("", true)
      ]),
      (!_ctx.disabled)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            (!_ctx.loading)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  (showClearIcon.value)
                    ? (_openBlock(), _createBlock(SmIcon, {
                        key: 0,
                        name: "Cross",
                        class: "select__icon select__icon--clear",
                        onClick: _withModifiers(handleClear, ["stop"])
                      }, null, 8, ["onClick"]))
                    : _createCommentVNode("", true),
                  _createVNode(SmIcon, {
                    name: toggleIcon.value,
                    class: "select__icon select__icon--toggle"
                  }, null, 8, ["name"])
                ], 64))
              : (_openBlock(), _createBlock(DefaultPreloader, { key: 1 }))
          ]))
        : _createCommentVNode("", true),
      (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
        _withDirectives(_createElementVNode("div", {
          style: _normalizeStyle(dropdownStyles.value),
          role: "menu",
          class: "select__list-wrapper",
          onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"]))
        }, [
          _createElementVNode("ul", {
            ref_key: "listElement",
            ref: listElement,
            tabindex: "0",
            class: "select__list"
          }, [
            (hasSearch.value)
              ? (_openBlock(), _createElementBlock("li", _hoisted_5, [
                  _createVNode(SmInput, {
                    modelValue: searchString.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((searchString).value = $event)),
                    search: "",
                    readonly: !hasSearch.value,
                    autocomplete: "off",
                    placeholder: _ctx.placeholder,
                    disabled: _ctx.disabled,
                    onFocus: onShowList,
                    onInput: handleInput,
                    onKeydown: _withKeys(handleFocusList, ["down"]),
                    onKeyup: _cache[1] || (_cache[1] = _withKeys(($event: any) => (handleSelectItem(undefined)), ["enter"]))
                  }, null, 8, ["modelValue", "readonly", "placeholder", "disabled", "onKeydown"])
                ]))
              : _createCommentVNode("", true),
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filteredOptions.value, (item, index) => {
              return (_openBlock(), _createElementBlock("li", {
                key: `list-item-${index}`,
                role: "menuitem",
                tabindex: "0",
                class: _normalizeClass(["select__list-item", { 'select__list-item--active': isFirstItemHighlighted(index) }]),
                onClick: ($event: any) => (handleSelectItem(item)),
                onKeyup: _withKeys(($event: any) => (handleSelectItem(item)), ["enter"])
              }, [
                _renderSlot(_ctx.$slots, "searchItem", { item: item }, () => [
                  _createElementVNode("div", _hoisted_7, [
                    _createElementVNode("p", {
                      innerHTML: item.title,
                      class: "select__item-title"
                    }, null, 8, _hoisted_8),
                    (item.subtitle)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(item.subtitle), 1))
                      : _createCommentVNode("", true)
                  ]),
                  (isItemSelected(item))
                    ? (_openBlock(), _createBlock(SmIcon, {
                        key: 0,
                        name: "Check",
                        class: "select__item-icon"
                      }))
                    : _createCommentVNode("", true)
                ])
              ], 42, _hoisted_6))
            }), 128))
          ], 512),
          (isMultiselectWithSearch.value && list.value.length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                _createVNode(SmTextButton, {
                  type: "primary",
                  onClick: handleSelectAll
                }, {
                  default: _withCtx(() => [
                    _createVNode(SmIcon, { name: "Plus" }),
                    _createTextVNode(" Выбрать все (" + _toDisplayString(list.value.length) + ") ", 1)
                  ]),
                  _: 1
                })
              ]))
            : _createCommentVNode("", true),
          _renderSlot(_ctx.$slots, "dropdown-footer")
        ], 4), [
          [_vShow, showList.value]
        ])
      ]))
    ], 4),
    (hasHint.value && !hasError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(_ctx.hint), 1))
      : _createCommentVNode("", true),
    (hasError.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_12, _toDisplayString(_ctx.error), 1))
      : _createCommentVNode("", true)
  ], 34)), [
    [_directive_click_outside, onHideList]
  ])
}
}

})